/* ============================================================================
   AboutPage (about-page-reskin) — the client About-the-Studio focused screen.
   Pinned bare-photo hero + rising sheet (mockup docs/mockups/client-dashboard-
   redesign/about-page.html; BookingDetailPage sheet idiom). Colors are the
   INJECTED per-tenant theme tokens ONLY — no :root hex, no hardcoded hex. The two
   surviving rgba literals are shipped BookingDetailPage precedents (the sanctioned
   sheet-shadow rgba(0,0,0,0.24) + the cream grab-handle) — b-cwr-hardcoded-theme-
   literals owns the sweep.
   ============================================================================ */

.page {
  position: fixed;
  inset: 0;
  background: var(--bg-page);
  color: var(--text-primary);
  overflow: hidden;
}

.scroll {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  z-index: 2;
  padding-bottom: calc(var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 40px);
  overscroll-behavior: none;
}
.scroll::-webkit-scrollbar {
  display: none;
}

/* ── Pinned hero + scrim ── */
.hero {
  position: sticky;
  top: 0;
  z-index: 0;
  height: 300px;
  background-size: cover;
  background-position: center;
}
/* Scrim: keep the photo readable up top, dissolve into the page at the sheet line. */
.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--bg-page-rgb), 0.3) 0%,
    rgba(var(--bg-page-rgb), 0) 34%,
    rgba(var(--bg-page-rgb), 0.55) 76%,
    var(--bg-page) 100%
  );
}
/* Gradient-fallback hero (no aboutImageUrl) — the shipped .aboutPhotoFallback ember
   composition at hero scale (the mockup's #1a0d05 deep endpoint is dropped for
   --accent-shadow to honor the no-hex rule; b-cwr-hardcoded-theme-literals owns a
   future dark-endpoint token). */
.heroFallback {
  background: radial-gradient(
      circle at 32% 26%,
      rgba(var(--accent-hot-rgb), 0.55) 0%,
      rgba(var(--accent-deep-rgb), 0.42) 46%,
      rgba(var(--accent-shadow-rgb), 0.3) 100%
    ),
    linear-gradient(160deg, var(--accent-deep) 0%, var(--accent-shadow) 100%);
}

/* ── Rising sheet ── */
.sheet {
  position: relative;
  z-index: 1;
  margin-top: -32px;
  border-radius: 48px 48px 0 0;
  background: var(--bg-page);
  /* Upward only: the negative spread keeps the blur from spilling past the flat
     bottom edge, which drew a dark band under a short sheet on non-black looks. */
  box-shadow: 0 -10px 16px -6px rgba(0, 0, 0, 0.24);
  min-height: 520px;
  padding-bottom: 8px;
}
.grab {
  width: 38px;
  height: 5px;
  border-radius: 3px;
  background: rgba(var(--fg-page-rgb), 0.22);
  margin: 12px auto 0;
}

/* ── Sheet head ── */
.head {
  padding: 18px 20px 0;
}
.kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 7px;
}
.name {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 33px;
  letter-spacing: -0.015em;
  line-height: 1.02;
  color: var(--text-primary);
}
.tagline {
  margin-top: 11px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary);
}
.meta {
  margin-top: 13px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: var(--text-tertiary);
}
.meta svg {
  width: 13px;
  height: 13px;
  stroke-width: 1.85;
  color: var(--accent);
}
.metaSep {
  opacity: 0.5;
}

/* ── Editorial sections ── */
.section {
  padding: 0 20px;
  margin-top: 28px;
}
.sectionTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.012em;
  color: var(--text-primary);
  margin-bottom: 11px;
}
.body {
  font-size: 14px;
  line-height: 1.62;
  color: var(--text-secondary);
}
.body + .body {
  margin-top: 11px;
}

/* ── Meet your guide ── */
.guide {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.guidePhoto {
  width: 66px;
  height: 66px;
  flex-shrink: 0;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1.5px rgba(var(--accent-rgb), 0.42);
}
.guidePhotoFallback {
  background-image: radial-gradient(circle at 30% 28%, var(--accent-hot) 0%, var(--accent-deep) 100%);
}
.guideName {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.012em;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 3px;
}
.guideRole {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--accent-hot);
  margin-bottom: 3px;
}
.guideCreds {
  font-size: 11.5px;
  color: var(--text-tertiary);
  letter-spacing: 0.01em;
  line-height: 1.35;
}

/* ── Book a Session — inline editorial closer (accent-orb; scrolls with content) ── */
.ctaWrap {
  padding: 30px 20px 4px;
}
.cta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 15px;
  border: 0;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: var(--glow-amber);
}
.cta svg {
  width: 17px;
  height: 17px;
  stroke-width: 2.6;
}
